import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import image0416 from '../assets/products/IMG_0416.PNG';
import image0508 from '../assets/products/IMG_0508.PNG';
import image0502 from '../assets/products/IMG_0502.JPG';
import image0192 from '../assets/products/IMG_0192.PNG';
import image9887 from '../assets/products/IMG_9887.PNG';
import image0413 from '../assets/products/IMG_0413.PNG';
import image1052 from '../assets/products/IMG_1052.PNG';
import image9869 from '../assets/products/IMG_9869.PNG';
import image0846 from '../assets/products/IMG_0846.PNG';
import image0843 from '../assets/products/IMG_0843.PNG';

const Products: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });
  const [activeIndex, setActiveIndex] = useState(0);

  const products = useMemo(
    () => [
      { image: image0416, title: 'Meze Çeşitleri', subtitle: 'Günlük taze üretim' },
      { image: image0508, title: 'Börek Çeşitleri', subtitle: 'Özel günlerinize zarif dokunuş' },
      { image: image0502, title: 'Special Pastalar', subtitle: 'Şef dokunuşlu premium reçeteler' },
      { image: image0192, title: 'Kurabiye Çeşitleri', subtitle: 'Güne sıcak başlangıç' },
      { image: image9887, title: 'Tatlı Vitrini', subtitle: 'Katmanlı, dengeli ve şık sunum' },
      { image: image0413, title: 'Meze Çeşitleri', subtitle: 'Köklerden gelen ustalık' },
      { image: image1052, title: 'Baklava Çeşitleri', subtitle: 'Toplantı ve davetler için ideal' },
      { image: image9869, title: 'Pastalar', subtitle: 'El yapımı, taze ve aromatik' },
      { image: image0846, title: 'Special Serisi', subtitle: 'Yüksek kalite malzeme, rafine tat' },
      { image: image0843, title: 'Sezon Seçkileri', subtitle: 'Döneme özel sınırlı ürünler' },
    ],
    []
  );

  const nextSlide = useCallback(() => {
    setActiveIndex((prev) => (prev + 1) % products.length);
  }, [products.length]);

  const prevSlide = useCallback(() => {
    setActiveIndex((prev) => (prev - 1 + products.length) % products.length);
  }, [products.length]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      nextSlide();
    }, 4500);

    return () => window.clearInterval(timer);
  }, [nextSlide]);

  return (
    <section id="products" className="py-20 md:py-32 bg-gradient-to-b from-[#14100e] to-[#1b1512] relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10 max-w-6xl">
        <div className="mx-auto">
          {/* Header */}
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="text-center mb-14 md:mb-24"
          >
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
              className="text-4xl md:text-7xl font-light mb-8 md:mb-12 leading-none tracking-tight text-amber-50"
            >
              Lezzetli
              <br />
              <span className="font-normal text-amber-300/80">Ürünlerimiz</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.2, delay: 0.4, ease: "easeOut" }}
              className="text-base sm:text-lg md:text-2xl text-amber-100/75 mb-10 md:mb-16 max-w-3xl mx-auto leading-relaxed font-light"
            >
              Geleneksel tariflerimizle hazırladığımız taze ve lezzetli ürünlerimizi keşfedin
            </motion.p>
          </motion.div>

          {/* Product Slider */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.6, ease: "easeOut" }}
            className="mb-20"
          >
            <div className="relative rounded-3xl overflow-hidden border border-amber-100/15 bg-black/20 shadow-2xl shadow-black/40">
              <div className="relative aspect-[16/9] md:aspect-[2/1]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={products[activeIndex].image}
                    src={products[activeIndex].image}
                    alt={products[activeIndex].title}
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </AnimatePresence>

                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />

                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-10">
                  <span className="inline-block text-[10px] sm:text-xs uppercase tracking-[0.16em] sm:tracking-[0.25em] text-amber-200/80 mb-2 md:mb-3">
                    Kaçkar Karafırın Seçkisi
                  </span>
                  <h3 className="text-lg sm:text-2xl md:text-4xl font-light text-amber-50 mb-1 md:mb-2">
                    {products[activeIndex].title}
                  </h3>
                  <p className="text-amber-100/80 text-xs sm:text-sm md:text-base font-light">
                    {products[activeIndex].subtitle}
                  </p>
                </div>

                <button
                  onClick={prevSlide}
                  className="absolute left-2 sm:left-4 md:left-6 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 md:p-3 rounded-full bg-black/45 border border-amber-100/30 text-amber-50 hover:bg-black/65 transition-colors"
                  aria-label="Önceki ürün görseli"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  onClick={nextSlide}
                  className="absolute right-2 sm:right-4 md:right-6 top-1/2 -translate-y-1/2 p-1.5 sm:p-2 md:p-3 rounded-full bg-black/45 border border-amber-100/30 text-amber-50 hover:bg-black/65 transition-colors"
                  aria-label="Sonraki ürün görseli"
                >
                  <ChevronRight size={18} />
                </button>
              </div>

              <div className="px-4 md:px-6 py-4 border-t border-amber-100/10 bg-[#110d0b]/90">
                <div className="flex gap-2 md:gap-3 overflow-x-auto pb-1">
                  {products.map((item, index) => (
                    <button
                      key={item.image}
                      onClick={() => setActiveIndex(index)}
                      className={`relative h-12 w-16 md:h-14 md:w-20 rounded-lg overflow-hidden border transition-all ${
                        activeIndex === index
                          ? 'border-amber-200/80 ring-1 ring-amber-200/70'
                          : 'border-amber-100/20 opacity-70 hover:opacity-100'
                      }`}
                      aria-label={`${item.title} görseline git`}
                    >
                      <img src={item.image} alt={item.title} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
                <div className="flex justify-center mt-4 gap-2">
                  {products.map((item, index) => (
                    <button
                      key={`${item.title}-dot`}
                      onClick={() => setActiveIndex(index)}
                      className={`h-2 rounded-full transition-all ${
                        activeIndex === index ? 'w-7 bg-amber-200' : 'w-2 bg-amber-100/40 hover:bg-amber-100/70'
                      }`}
                      aria-label={`${index + 1}. görsele git`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Manifesto */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.8, ease: "easeOut" }}
            className="text-center"
          >
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 1.2, delay: 1, ease: "easeOut" }}
              className="text-xl md:text-4xl font-light text-amber-100/85 leading-relaxed max-w-4xl mx-auto"
            >
              "Her ürünümüzde sevgi, emek ve 
              <br />
              geleneksel tariflerimiz gizli."
            </motion.p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Products; 